import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings, LogOut, User, Clock, CreditCard, Lock } from 'lucide-react';
import axios from 'axios';
import CaminoDelMedico from './CaminoDelMedico';
import GenderSelector from './GenderSelector';
import MenuGrid from './MenuGrid';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

const apiClient = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  headers: {
    'Content-Type': 'application/json'
  }
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const MainHub = ({ user, onLogout, onUserUpdate }) => {
  const navigate = useNavigate();
  const [showMenu, setShowMenu] = useState(false);
  const [pendingDuelsCount, setPendingDuelsCount] = useState(0);
  const [pendingCardChallengesCount, setPendingCardChallengesCount] = useState(0);
  const [showGenderSelector, setShowGenderSelector] = useState(!user?.gender);
  const audioRef = useRef(null);
  const [hasNewPublication, setHasNewPublication] = useState(false);
  const [journalSeenKey, setJournalSeenKey] = useState(null);
  const [supportUnreadCount, setSupportUnreadCount] = useState(0);
  const [trialTimeLeft, setTrialTimeLeft] = useState(null);
  const [payingFromBanner, setPayingFromBanner] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Limited role (curso intensivo): only these sections are accessible
  const isLimited = user?.role === 'limited';
  const LIMITED_ALLOWED = ['simulacros', 'duelos', 'juego-cartas', 'ranking', 'journal'];
  const lockedIds = isLimited
    ? new Set(['planificador', 'cuestionarios', 'escape-room', 'imagen-dx', 'presentaciones', 'flashcards', 'enarm-match', 'soporte'])
    : new Set();

  // Trial countdown timer
  useEffect(() => {
    if (user?.account_type !== 'trial' || !user?.subscription_expires) return;
    
    const updateCountdown = () => {
      const now = new Date();
      const expires = new Date(user.subscription_expires);
      const diff = expires - now;
      
      if (diff <= 0) {
        setTrialTimeLeft({ expired: true });
        return;
      }
      
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      setTrialTimeLeft({ hours, minutes, seconds, expired: false });
    };
    
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [user?.account_type, user?.subscription_expires]);

  const handleTrialPayNow = async () => {
    setPayingFromBanner(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/api/payments/create-preference`, { user_id: user.id });
      if (res.data.init_point) {
        window.location.href = res.data.init_point;
        return;
      }
    } catch (err) {
      console.error('Payment error:', err);
    }
    setPayingFromBanner(false);
  };

  // Fetch pending duels count
  useEffect(() => {
    const fetchPendingCount = async () => {
      try {
        const response = await apiClient.get('/duels/pending/count');
        setPendingDuelsCount(response.data.count || 0);
      } catch (error) {
        console.error('Error fetching pending duels count:', error);
      }
    };
    
    fetchPendingCount();
    const interval = setInterval(fetchPendingCount, 30000);
    return () => clearInterval(interval);
  }, []);

  // Fetch pending card game challenges count
  useEffect(() => {
    const fetchCardChallengesCount = async () => {
      try {
        const response = await apiClient.get('/card-game/challenges/pending/count');
        setPendingCardChallengesCount(response.data.count || 0);
      } catch (error) {
        console.error('Error fetching card challenges count:', error);
      }
    };
    
    fetchCardChallengesCount();
    const interval = setInterval(fetchCardChallengesCount, 30000);
    return () => clearInterval(interval);
  }, []);

  // Check if user is author in today's journal
  useEffect(() => {
    const checkJournalAuthor = async () => {
      try {
        const response = await apiClient.get('/journal/today');
        const journal = response.data;
        if (journal && journal.authors) {
          const isAuthor = journal.authors.some(a => a.user_id === user.id || a.full_name === user.full_name);
          if (isAuthor) {
            const key = `journal_seen_${journal.issue_number || journal.date_str}`;
            setJournalSeenKey(key);
            const alreadySeen = localStorage.getItem(key);
            setHasNewPublication(!alreadySeen);
          }
        }
      } catch (error) {
        console.log('Journal check skipped');
      }
    };
    checkJournalAuthor();
  }, [user.id, user.full_name]);

  // Fetch support unread count
  useEffect(() => {
    const fetchSupportUnread = async () => {
      try {
        const res = await apiClient.get('/support/unread-count');
        setSupportUnreadCount(res.data.count || 0);
      } catch (error) {
        console.log('Support unread check skipped');
      }
    };
    fetchSupportUnread();
    const interval = setInterval(fetchSupportUnread, 30000);
    return () => clearInterval(interval);
  }, []);

  // Play pencil scribble MP3 sound
  const playPencilSound = () => {
    try {
      const audio = new Audio('https://customer-assets.emergentagent.com/job_13d67352-1aa7-430c-b46e-966b83d4fc8c/artifacts/wfi9fn7o_pencil_scribble_7-389260.mp3');
      audio.volume = 1.0;
      audio.play();
    } catch (error) {
      console.log('Audio not supported');
    }
  };

  const menuItems = [
    {
      id: 'planificador',
      label: 'Planificador inteligente de estudio',
      route: '/planificador'
    },
    {
      id: 'cuestionarios',
      label: 'Cuestionarios GPCs',
      route: '/cuestionarios'
    },
    {
      id: 'masterclasses',
      icon: 'cuestionarios',
      label: 'Masterclasses Clínicas Guiadas',
      route: '/masterclasses'
    },
    {
      id: 'escape-room',
      label: 'Casos clínicos Escape Room',
      route: '/escape-room'
    },
    {
      id: 'imagen-dx',
      label: 'Imágenes diagnósticas',
      route: '/imagen-dx'
    },
    {
      id: 'presentaciones',
      label: 'Presentaciones de repaso',
      route: '/presentaciones'
    },
    {
      id: 'flashcards',
      label: 'Flash cards personales',
      route: '/flashcards'
    },
    {
      id: 'simulacros',
      label: 'Simulacros',
      route: '/simulacros'
    },
    {
      id: 'duelos',
      label: 'Duelos',
      route: '/duels'
    },
    {
      id: 'juego-cartas',
      label: 'Juego de Cartas',
      route: '/juego-cartas'
    },
    {
      id: 'ranking',
      label: 'Ranking del Día',
      route: '/leaderboard'
    },
    {
      id: 'journal',
      label: 'Journal del Día',
      route: '/journal'
    },
    {
      id: 'enarm-match',
      label: 'Selector de Especialidad',
      route: '/enarm-match'
    },
    {
      id: 'soporte',
      label: 'Sugerencias y Soporte',
      route: '/soporte'
    }
  ];

  // Handle scroll to show/hide menu
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const triggerPoint = window.innerHeight * 0.3;
      setShowMenu(scrollPosition > triggerPoint);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (item) => {
    // item is the full menu item ({ id, label, route })
    const route = typeof item === 'string' ? item : item.route;
    const id = typeof item === 'string' ? null : item.id;
    // Block locked sections for limited users → show upgrade modal
    if (isLimited && id && lockedIds.has(id)) {
      setShowUpgradeModal(true);
      return;
    }
    playPencilSound();
    // Mark journal as seen immediately when navigating to it
    if (route === '/journal' && hasNewPublication && journalSeenKey) {
      localStorage.setItem(journalSeenKey, 'true');
      setHasNewPublication(false);
    }
    setTimeout(() => {
      navigate(route);
    }, 100);
  };

  const handleGenderSet = (gender) => {
    setShowGenderSelector(false);
    if (onUserUpdate) onUserUpdate({ ...user, gender });
  };

  return (
    <div className="min-h-[200vh] bg-white">
      {/* Gender selector modal for existing users */}
      {showGenderSelector && (
        <GenderSelector onGenderSet={handleGenderSet} />
      )}
      {/* Header - Fixed at top */}
      <header className="fixed top-0 left-0 right-0 py-4 px-4 z-50 bg-white">
        <div className="max-w-7xl mx-auto relative">
          {/* User photo and name - centered */}
          <div className="flex flex-col items-center justify-center">
            {user.profile_image ? (
              <img
                src={user.profile_image}
                alt={user.full_name}
                className="w-16 h-16 rounded-full object-cover border-2 border-black"
              />
            ) : (
              <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center text-black font-bold text-xl border-2 border-black">
                {user.full_name.charAt(0).toUpperCase()}
              </div>
            )}
            <span className="text-sm font-semibold text-black mt-1">{user.full_name}</span>
          </div>
          
          {/* Admin and Logout buttons - absolute right */}
          <div className="absolute right-0 top-1/2 -translate-y-1/2 flex items-center space-x-2">
            <button
              onClick={() => navigate('/perfil')}
              className="bg-white rounded-full p-1.5 border border-black hover:bg-gray-100 transition-all"
              data-testid="profile-btn"
            >
              <User size={14} className="text-black" />
            </button>
            {user.is_admin && (
              <button
                onClick={() => navigate('/admin')}
                className="bg-white rounded-full p-1.5 border border-black hover:bg-gray-100 transition-all"
                data-testid="admin-open-panel-btn"
              >
                <Settings size={14} className="text-black" />
              </button>
            )}
            
            <button
              onClick={onLogout}
              className="bg-white rounded-full p-1.5 border border-black hover:bg-gray-100 transition-all"
            >
              <LogOut size={14} className="text-black" />
            </button>
          </div>
        </div>
      </header>

      {/* Trial Countdown Banner */}
      {trialTimeLeft && !trialTimeLeft.expired && !user?.is_admin && (
        <div className="fixed top-[88px] left-0 right-0 z-50 px-4" data-testid="trial-countdown-banner">
          <div className="max-w-lg mx-auto bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5 flex items-center justify-between gap-3 shadow-sm">
            <div className="flex items-center gap-2 min-w-0">
              <Clock size={16} className="text-amber-600 flex-shrink-0" />
              <span className="text-sm text-amber-800 font-medium truncate">
                Prueba gratuita:
              </span>
              <span className="text-sm font-bold text-amber-900 tabular-nums flex-shrink-0" data-testid="trial-countdown-time">
                {String(trialTimeLeft.hours).padStart(2, '0')}:{String(trialTimeLeft.minutes).padStart(2, '0')}:{String(trialTimeLeft.seconds).padStart(2, '0')}
              </span>
            </div>
            <button
              onClick={handleTrialPayNow}
              disabled={payingFromBanner}
              className="bg-black text-white text-xs font-semibold px-3 py-1.5 rounded-full hover:bg-gray-800 transition-all flex items-center gap-1 flex-shrink-0"
              data-testid="trial-upgrade-btn"
            >
              <CreditCard size={12} />
              {payingFromBanner ? '...' : 'Pagar'}
            </button>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <div className="min-h-screen flex flex-col items-center px-1 pb-8 pt-2 sm:justify-center sm:pt-0">
        {/* Main logo/image - bigger on mobile via CSS crop */}
        <div className="flex-1 sm:flex-none flex items-center justify-center w-full">
          {/* Mobile: zoomed in crop */}
          <div className="block sm:hidden w-full overflow-hidden" style={{ maxHeight: '280px' }}>
            <img
              src="https://customer-assets.emergentagent.com/job_13d67352-1aa7-430c-b46e-966b83d4fc8c/artifacts/kmnn6rz3_proyectoresident.png"
              alt="Proyecto Residente"
              className="h-auto animate-fadeIn"
              style={{ 
                width: '170%', 
                maxWidth: 'none',
                marginLeft: '-35%',
                marginTop: '-5%'
              }}
            />
          </div>
          {/* Desktop: normal */}
          <img
            src="https://customer-assets.emergentagent.com/job_13d67352-1aa7-430c-b46e-966b83d4fc8c/artifacts/kmnn6rz3_proyectoresident.png"
            alt="Proyecto Residente"
            className="hidden sm:block h-auto animate-fadeIn max-w-[800px] w-full"
          />
        </div>
        
        {/* Camino del Médico - Progress System */}
        <div className="w-full max-w-3xl mx-auto animate-fadeIn flex-shrink-0" style={{ animationDelay: '0.3s' }}>
          <CaminoDelMedico user={user} />
        </div>

        {/* Scroll indicator */}
        <div className="mt-4 mb-4 flex-shrink-0 animate-bounce">
          <div className="w-6 h-10 border-2 border-gray-400 rounded-full flex items-start justify-center p-1 mx-auto">
            <div className="w-1.5 h-3 bg-gray-400 rounded-full"></div>
          </div>
          <p className="text-gray-400 text-xs mt-1 text-center">Scroll</p>
        </div>
      </div>

      {/* Navigation Menu - Appears on scroll */}
      <div 
        className={`fixed inset-0 bg-white/95 backdrop-blur-sm transition-all duration-500 z-40 overflow-y-auto ${
          showMenu ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div className="w-full pt-36 pb-10 sm:pt-40">
          <MenuGrid
            menuItems={menuItems}
            onNavClick={handleNavClick}
            lockedIds={lockedIds}
            pendingDuelsCount={pendingDuelsCount}
            pendingCardChallengesCount={pendingCardChallengesCount}
            hasNewPublication={hasNewPublication}
            supportUnreadCount={supportUnreadCount}
            visible={showMenu}
          />
        </div>
      </div>

      {/* Upgrade modal for locked sections (limited role) */}
      {showUpgradeModal && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4"
          data-testid="upgrade-modal"
          onClick={() => setShowUpgradeModal(false)}
        >
          <div
            className="bg-white rounded-2xl max-w-sm w-full p-6 text-center shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
              <Lock size={26} className="text-black" />
            </div>
            <p className="text-base font-semibold text-black leading-snug mb-5" data-testid="upgrade-modal-text">
              Accede a todas las funciones de Proyecto Residente inscribiéndote desde proyectoresidente.com
            </p>
            <button
              onClick={() => { setShowUpgradeModal(false); navigate('/landing'); }}
              className="block w-full bg-black text-white rounded-full py-3 font-semibold text-sm hover:bg-gray-800 transition-colors"
              data-testid="upgrade-modal-cta"
            >
              Obtener acceso completo
            </button>
            <button
              onClick={() => setShowUpgradeModal(false)}
              className="mt-3 text-sm text-gray-500 hover:text-black"
              data-testid="upgrade-modal-close"
            >
              Cerrar
            </button>
          </div>
        </div>
      )}

      {/* Custom styles for animations */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.8s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default MainHub;
